import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import snippetsService, { type Snippet, type CreateSnippetData, type UpdateSnippetData } from '@/services/snippets.service';

export const SNIPPETS_QUERY_KEYS = {
  all: ['snippets'] as const,
  lists: () => ['snippets', 'list'] as const,
  list: (params: any) => ['snippets', 'list', params] as const,
  details: () => ['snippets', 'detail'] as const,
  detail: (id: string) => ['snippets', 'detail', id] as const,
  categories: () => ['snippets', 'categories'] as const,
  shortcuts: () => ['snippets', 'shortcuts'] as const,
};

// Get all snippets with filters
export function useSnippets(params: {
  category?: string;
  is_global?: boolean;
  search?: string;
  page?: number;
  limit?: number;
} = {}) {
  return useQuery({
    queryKey: SNIPPETS_QUERY_KEYS.list(params),
    queryFn: () => snippetsService.getSnippets(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

// Get single snippet by ID
export function useSnippet(id: string) {
  return useQuery({
    queryKey: SNIPPETS_QUERY_KEYS.detail(id),
    queryFn: () => snippetsService.getSnippetById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// Get snippets by category
export function useSnippetsByCategory(category: string) {
  return useQuery({
    queryKey: ['snippets', 'category', category],
    queryFn: () => snippetsService.getSnippetsByCategory(category),
    enabled: !!category,
    staleTime: 1000 * 60 * 5,
  });
}

// Search snippets
export function useSearchSnippets(query: string) {
  return useQuery({
    queryKey: ['snippets', 'search', query],
    queryFn: () => snippetsService.searchSnippets(query),
    enabled: !!query && query.length >= 2,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// Get shortcuts for autocomplete
export function useSnippetShortcuts() {
  return useQuery({
    queryKey: SNIPPETS_QUERY_KEYS.shortcuts(),
    queryFn: () => snippetsService.getShortcuts(),
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

// Create snippet mutation
export function useCreateSnippet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSnippetData) => snippetsService.createSnippet(data),
    onSuccess: () => {
      // Invalidate all snippet queries
      queryClient.invalidateQueries({ queryKey: SNIPPETS_QUERY_KEYS.all });
    },
    onError: (error) => {
      console.error('Failed to create snippet:', error);
    },
  });
}

// Update snippet mutation
export function useUpdateSnippet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSnippetData }) =>
      snippetsService.updateSnippet(id, data),
    onSuccess: (response, variables) => {
      // Update the snippet in cache
      queryClient.setQueryData(
        SNIPPETS_QUERY_KEYS.detail(variables.id),
        response
      );
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: SNIPPETS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('Failed to update snippet:', error);
    },
  });
}

// Delete snippet mutation
export function useDeleteSnippet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => snippetsService.deleteSnippet(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: SNIPPETS_QUERY_KEYS.detail(deletedId) });
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: SNIPPETS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('Failed to delete snippet:', error);
    },
  });
}

// Use snippet mutation (increment usage count and get processed content)
export function useSnippetUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, variables }: { id: string; variables?: Record<string, string> }) =>
      snippetsService.useSnippet(id, variables),
    onSuccess: (response, variables) => {
      // Update usage count in cache if we have the snippet cached
      queryClient.setQueryData(
        SNIPPETS_QUERY_KEYS.detail(variables.id),
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              snippet: {
                ...oldData.data.snippet,
                usageCount: oldData.data.snippet.usageCount + 1,
                lastUsedAt: new Date().toISOString(),
              }
            }
          };
        }
      );

      // Invalidate list to refresh usage counts
      queryClient.invalidateQueries({ queryKey: SNIPPETS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('Failed to use snippet:', error);
    },
  });
}